import { useState, type FormEvent } from 'react';
import { Aviso, Boton, Campo, ENTRADA, SelectorTema } from '../../components/ui';
import { TEMA_POR_DEFECTO, type Tema } from '../../nucleo/temas';
import { validarTorneo, type DatosTorneo } from '../../nucleo/validar';
import { OPCIONES_INICIALES, validarOpciones } from '../../nucleo/opciones';
import { CamposOpciones } from './OpcionesTorneo';
import { Desplegable } from '../../components/Desplegable';
import { mensajeDe } from '../../services/api';

/** Los datos de un torneo: lo usan "Nuevo torneo" y "Editar datos". */
export function FormTorneo({
  inicial,
  textoBoton,
  alGuardar,
  alCancelar,
}: {
  inicial?: DatosTorneo;
  textoBoton: string;
  alGuardar: (d: DatosTorneo) => Promise<void>;
  alCancelar?: () => void;
}) {
  const [opciones, setOpciones] = useState(inicial?.opciones ?? OPCIONES_INICIALES);
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [sede, setSede] = useState(inicial?.sede ?? '');
  const [desde, setDesde] = useState(inicial?.fecha_desde ?? '');
  const [hasta, setHasta] = useState(inicial?.fecha_hasta ?? '');
  const [tema, setTema] = useState<Tema>(inicial?.tema ?? TEMA_POR_DEFECTO);
  const [canchas, setCanchas] = useState(inicial?.canchas?.toString() ?? '');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    // La misma validación que la API: avisa antes de mandar.
    const datos = validarTorneo({ nombre, sede, fecha_desde: desde, fecha_hasta: hasta || desde, tema, canchas: canchas ? Number(canchas) : null });
    if (!datos.ok) return setError(datos.error);
    const op = validarOpciones(opciones);
    if (!inicial && !op.ok) return setError(op.error);
    setError('');
    setEnviando(true);
    try {
      await alGuardar({...datos.valor, ...(!inicial ? {opciones} : {})});
    } catch (err) {
      setError(mensajeDe(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form onSubmit={enviar} className="flex flex-col gap-3">
      <Campo etiqueta="Nombre del torneo">
        <input className={ENTRADA} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Abierto de Octubre" />
      </Campo>
      <Campo etiqueta="Sede (opcional)">
        <input className={ENTRADA} value={sede} onChange={(e) => setSede(e.target.value)} placeholder="Club La Esquina" />
      </Campo>
      <div className="grid grid-cols-2 gap-3">
        <Campo etiqueta="Desde">
          <input
            className={ENTRADA}
            type="date"
            value={desde}
            onChange={(e) => {
              setDesde(e.target.value);
              if (!hasta || hasta < e.target.value) setHasta(e.target.value);
            }}
          />
        </Campo>
        <Campo etiqueta="Hasta">
          <input className={ENTRADA} type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} />
        </Campo>
      </div>
      <Campo etiqueta="Canchas (opcional)" ayuda="Cuántas canchas tiene la sede. Hace falta para armar los horarios.">
        <input
          className={ENTRADA}
          inputMode="numeric"
          value={canchas}
          onChange={(e) => setCanchas(e.target.value.replace(/D/g, '').slice(0, 2))}
          placeholder="4"
        />
      </Campo>
      <Campo etiqueta="Colores" ayuda="Así se ve la página pública. Cada categoría puede tener el suyo.">
        <SelectorTema valor={tema} onChange={(t) => setTema(t ?? TEMA_POR_DEFECTO)} />
      </Campo>
      {!inicial && <Desplegable titulo="Opciones del torneo" resumen="Herramientas y horarios"><CamposOpciones valor={opciones} cambiar={setOpciones} ocupado={enviando} /></Desplegable>}
      {error && <Aviso tono="error">{error}</Aviso>}
      <div className="flex gap-2">
        <Boton type="submit" deshabilitado={enviando}>
          {enviando ? 'Guardando...' : textoBoton}
        </Boton>
        {alCancelar && (
          <Boton tipo="secundario" onClick={alCancelar}>
            Cancelar
          </Boton>
        )}
      </div>
    </form>
  );
}
